import type { FormData } from "./types";

/**
 * Lead-form "When are you considering starting your programme?" handling.
 *
 * When a Degree lead picks an accelerated start intent ("3 to 6 months" or
 * "Next available start date"), the RV TYP redirect must carry `at=true` so the
 * thank-you experience mirrors the growth-ops setup. Any other answer keeps the
 * base TYP URL.
 *
 * Routing is delegated to the Taxi LeadFormPlugin's native `thankYouPageURLs`
 * map (`{ [fieldIndex]: { [answerValue]: url } }`), which the plugin resolves on
 * submit. The plugin snapshots `thankYouURL` at construction, so per-answer
 * routing must live in the form package rather than be mutated at runtime.
 */

const START_TIMEFRAME_FIELD_NAME = "prospect_education_journey";

// Wording-agnostic so both "program" (US) and "programme" (UK) labels match.
const START_TIMEFRAME_QUESTION = "considering starting your";

const ACCELERATED_ANSWERS = ["3 to 6 months", "next available start date"];

const AT_PARAM = "at";

const normalize = (value: string): string =>
  value.toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();

/**
 * True when an answer to the start-timeframe question is one of the accelerated
 * options that should flag the lead with `at=true`.
 */
export function isAcceleratedStartAnswer(
  answer: string | null | undefined,
): boolean {
  if (!answer) {
    return false;
  }
  const normalized = normalize(answer);
  return ACCELERATED_ANSWERS.some((option) => normalized.includes(option));
}

/**
 * Adds or removes `at=true` on a post-submit URL based on the accelerated flag.
 *
 * Preserves existing query params and hash, and keeps relative (same-origin) URLs
 * relative so the RV TYP rewrite produced by `modifyDegreePostSubmitUrl` is not
 * promoted to an absolute partner origin. No-op when the URL is missing/invalid.
 */
export function applyStartTimeframeParam(
  url: string,
  accelerated: boolean,
): string {
  if (!url) {
    return url;
  }

  const isAbsolute = /^[a-z][a-z\d+\-.]*:\/\//i.test(url);
  let parsed: URL;
  try {
    parsed = new URL(url, isAbsolute ? undefined : "https://relative.invalid");
  } catch {
    return url;
  }

  if (accelerated) {
    parsed.searchParams.set(AT_PARAM, "true");
  } else {
    parsed.searchParams.delete(AT_PARAM);
  }

  if (isAbsolute) {
    return parsed.toString();
  }

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

type TaxiFieldOption = { label?: string; value?: string };

type TaxiField = {
  name?: string;
  label?: string;
  value?: { options?: TaxiFieldOption[] };
};

const isStartTimeframeField = (field: unknown): field is TaxiField => {
  if (!field || typeof field !== "object") {
    return false;
  }
  const candidate = field as TaxiField;
  if (candidate.name === START_TIMEFRAME_FIELD_NAME) {
    return true;
  }
  const label =
    typeof candidate.label === "string" ? normalize(candidate.label) : "";
  return label.includes(START_TIMEFRAME_QUESTION);
};

/**
 * Injects answer-based TYP routing into a Taxi form package so the plugin sends
 * accelerated-start leads to the `at=true` variant of the post-submit URL.
 *
 * Returns the original package unchanged when the start-timeframe question is
 * absent, has no accelerated options, or no base URL is provided, so control
 * traffic is byte-for-byte unchanged.
 */
export function injectStartTimeframeRouting(
  taxiFormData: FormData | null | undefined,
  baseUrl: string,
): FormData | null | undefined {
  if (!taxiFormData || !baseUrl) {
    return taxiFormData;
  }

  const fields = (taxiFormData as { fields?: unknown }).fields;
  if (!Array.isArray(fields)) {
    return taxiFormData;
  }

  const fieldIndex = fields.findIndex(isStartTimeframeField);
  if (fieldIndex === -1) {
    return taxiFormData;
  }

  const options = (fields[fieldIndex] as TaxiField)?.value?.options;
  if (!Array.isArray(options)) {
    return taxiFormData;
  }

  const acceleratedUrl = applyStartTimeframeParam(baseUrl, true);
  const answerMap: Record<string, string> = {};
  for (const option of options) {
    const answerValue = option?.value ?? option?.label;
    if (
      answerValue &&
      (isAcceleratedStartAnswer(option?.value) ||
        isAcceleratedStartAnswer(option?.label))
    ) {
      answerMap[String(answerValue)] = acceleratedUrl;
    }
  }

  if (Object.keys(answerMap).length === 0) {
    return taxiFormData;
  }

  const existingRouting =
    (taxiFormData as { thankYouPageURLs?: Record<string, unknown> })
      .thankYouPageURLs ?? {};

  return {
    ...taxiFormData,
    thankYouPageURLs: {
      ...existingRouting,
      [fieldIndex]: {
        ...((existingRouting[fieldIndex] as Record<string, string>) ?? {}),
        ...answerMap,
      },
    },
  };
}
